import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ATTEND_STATUS, SESSION_STATUS, todayStr, shortDate, dateLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Star, Repeat, Pencil, XCircle, BookOpen, Loader2 } from "lucide-react";
import { UnderstandingPicker, MaterialHistory } from "@/components/JournalExtras";
import { toast } from "sonner";

const ABSENT = ["izin", "sakit", "alpa", "lainnya"];

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" data-testid={`session-star-${i}`} onClick={() => onChange(i)}>
          <Star className={`w-7 h-7 ${i <= value ? "fill-amber-400 text-amber-400" : "text-slate-200 hover:text-amber-200"}`} />
        </button>
      ))}
    </div>
  );
}

export function SessionDialog({ session, onClose, onChanged }) {
  const [view, setView] = useState("main");
  const [note, setNote] = useState("");
  const [journal, setJournal] = useState({ material: "", homework: "", rating: 5, teacher_note: "", understanding: "paham" });
  const [res, setRes] = useState({ new_date: "", new_time: "", reason: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!session) return;
    setView("main"); setNote(session.attendance?.note || "");
    const j = session.journal;
    setJournal(j ? { material: j.material, homework: j.homework || "", rating: j.rating, teacher_note: j.teacher_note || "", understanding: j.understanding || "paham" } : { material: "", homework: "", rating: 5, teacher_note: "", understanding: "paham" });
    const r = session.reschedule;
    setRes(r ? { new_date: r.new_date, new_time: r.new_time || "", reason: r.reason || "" } : { new_date: "", new_time: session.time, reason: "" });
  }, [session]);

  if (!session) return null;
  const isToday = session.date === todayStr();
  const s = session;
  const final = ["hadir", "pengganti_hadir", "tidak_diikuti"].includes(s.status);
  const canReschedule = s.kind === "reguler" && ["belum", "tidak_hadir"].includes(s.status);
  const canMark = isToday && !final && s.status !== "pengganti" || (isToday && s.kind === "pengganti" && s.status === "pengganti");

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const mark = (status) => run(() => api.post("/attendance", { student_id: s.student_id, date: s.date, status, note }), `${s.name}: ${ATTEND_STATUS[status].label}`);
  const saveJournal = () => {
    if (!journal.material.trim()) return toast.error("Materi wajib diisi");
    return run(async () => {
      if (!s.attendance || s.attendance.status !== "hadir") await api.post("/attendance", { student_id: s.student_id, date: s.date, status: "hadir", note: "" });
      if (s.journal) await api.put(`/journals/${s.journal.id}`, journal);
      else await api.post("/journals", { ...journal, student_id: s.student_id, date: s.date });
    }, "Kehadiran & jurnal tersimpan 📚");
  };
  const saveRes = () => {
    if (!res.new_date) return toast.error("Pilih tanggal pengganti");
    return run(async () => {
      if (s.reschedule) await api.put(`/reschedules/${s.reschedule.id}`, res);
      else await api.post("/reschedules", { ...res, student_id: s.student_id, original_date: s.date });
    }, s.reschedule ? "Jadwal pengganti diperbarui" : "Jadwal pengganti dibuat");
  };
  const cancelRes = () => run(() => api.delete(`/reschedules/${s.reschedule.id}`), "Jadwal pengganti dibatalkan, sesi kembali ke awal");

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto gemma-scroll" data-testid="session-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{s.name}</DialogTitle>
          <div className="text-sm text-slate-500">{dateLabel(s.date)} · {s.time} {s.kind === "pengganti" && <span className="text-violet-600 font-semibold">· Sesi pengganti dari {shortDate(s.original_date)}</span>}</div>
          <Badge data-testid="session-status-badge" variant="outline" className={`rounded-full w-fit mt-1 ${SESSION_STATUS[s.status]?.cls}`}>{SESSION_STATUS[s.status]?.label}</Badge>
        </DialogHeader>

        {view === "main" && (
          <div className="space-y-4 py-2">
            {!isToday && !final && s.status !== "pengganti" && <p className="text-xs text-amber-700 bg-amber-50 rounded-xl p-3">Tandai kehadiran & jurnal hanya bisa dilakukan pada hari sesi berlangsung. Jadwal pengganti bisa diatur kapan saja.</p>}
            {s.status === "tidak_diikuti" && <p className="text-sm text-slate-500 bg-slate-50 rounded-xl p-3">Sesi ini dianggap tidak diikuti (pengganti juga tidak dihadiri / sudah lewat). Tidak ada jurnal dan tidak ada pengganti lagi.</p>}

            {canMark && (
              <div>
                <Label className="mb-2 block">Tandai Kehadiran</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button data-testid="mark-hadir-btn" onClick={() => setView("journal")} className="rounded-xl bg-emerald-500 hover:bg-emerald-600 col-span-2"><BookOpen className="w-4 h-4 mr-1" /> Hadir → Isi Jurnal Belajar</Button>
                  {ABSENT.map((o) => (
                    <Button key={o} data-testid={`mark-${o}-btn`} variant="outline" disabled={busy} onClick={() => mark(o)} className={`rounded-xl ${ATTEND_STATUS[o].cls}`}>{ATTEND_STATUS[o].label}</Button>
                  ))}
                </div>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)" className="mt-2 rounded-xl" />
              </div>
            )}

            {(s.status === "hadir" || s.status === "pengganti_hadir") && s.kind !== "pengganti" && s.status === "hadir" && (
              <Button data-testid="edit-journal-btn" variant="outline" onClick={() => setView("journal")} className="rounded-xl w-full"><Pencil className="w-4 h-4 mr-1" /> {s.journal ? "Edit Jurnal Belajar" : "Isi Jurnal Belajar"}</Button>
            )}
            {s.kind === "pengganti" && s.status === "hadir" && (
              <Button data-testid="edit-journal-btn" variant="outline" onClick={() => setView("journal")} className="rounded-xl w-full"><Pencil className="w-4 h-4 mr-1" /> {s.journal ? "Edit Jurnal Belajar" : "Isi Jurnal Belajar"}</Button>
            )}

            {canReschedule && (
              <Button data-testid="open-reschedule-btn" variant="outline" onClick={() => setView("reschedule")} className="rounded-xl w-full border-violet-300 text-violet-700 hover:bg-violet-50"><Repeat className="w-4 h-4 mr-1" /> Atur Jadwal Pengganti (1x)</Button>
            )}

            {s.reschedule && s.kind === "reguler" && s.status === "pengganti" && (
              <div className="rounded-xl bg-violet-50 border border-violet-100 p-3 space-y-2">
                <div className="text-sm"><span className="text-slate-500">Pengganti:</span> <span className="font-semibold text-violet-700">{dateLabel(s.reschedule.new_date)} · {s.reschedule.new_time}</span></div>
                {s.reschedule.reason && <div className="text-xs text-slate-500">Alasan: {s.reschedule.reason}</div>}
                {s.reschedule.editable ? (
                  <div className="flex gap-2">
                    <Button size="sm" data-testid="edit-reschedule-btn" variant="outline" onClick={() => setView("reschedule")} className="rounded-lg flex-1"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                    <Button size="sm" data-testid="cancel-reschedule-btn" variant="outline" disabled={busy} onClick={cancelRes} className="rounded-lg flex-1 text-rose-600 border-rose-200 hover:bg-rose-50"><XCircle className="w-3.5 h-3.5 mr-1" /> Batalkan</Button>
                  </div>
                ) : <div className="text-xs text-slate-400">Jadwal pengganti sudah lewat / sudah dicatat — tidak bisa diubah.</div>}
              </div>
            )}
          </div>
        )}

        {view === "journal" && (
          <div className="space-y-3 py-2">
            <MaterialHistory studentId={s.student_id} />
            <div><Label>Materi Belajar</Label><Input data-testid="session-material-input" value={journal.material} onChange={(e) => setJournal({ ...journal, material: e.target.value })} placeholder="cth: Iqro halaman 12" className="mt-1 rounded-xl" /></div>
            <div><Label>PR / Tugas</Label><Input value={journal.homework} onChange={(e) => setJournal({ ...journal, homework: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label>Level Pemahaman</Label><div className="mt-1"><UnderstandingPicker value={journal.understanding} onChange={(v) => setJournal({ ...journal, understanding: v })} /></div></div>
            <div><Label>Penilaian</Label><div className="mt-1"><Stars value={journal.rating} onChange={(v) => setJournal({ ...journal, rating: v })} /></div></div>
            <div><Label>Catatan Guru</Label><Textarea value={journal.teacher_note} onChange={(e) => setJournal({ ...journal, teacher_note: e.target.value })} rows={2} className="mt-1 rounded-xl" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setView("main")} className="rounded-xl">Kembali</Button>
              <Button data-testid="save-session-journal-btn" disabled={busy} onClick={saveJournal} className="rounded-xl bg-emerald-500 hover:bg-emerald-600">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Hadir + Jurnal"}</Button>
            </DialogFooter>
          </div>
        )}

        {view === "reschedule" && (
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal Pengganti</Label><Input type="date" data-testid="session-res-date" value={res.new_date} onChange={(e) => setRes({ ...res, new_date: e.target.value })} className="mt-1 rounded-xl" /></div>
              <div><Label>Jam</Label><Input type="time" value={res.new_time} onChange={(e) => setRes({ ...res, new_time: e.target.value })} className="mt-1 rounded-xl" /></div>
            </div>
            <div><Label>Alasan</Label><Textarea value={res.reason} onChange={(e) => setRes({ ...res, reason: e.target.value })} rows={2} placeholder="cth: Orang tua konfirmasi berhalangan" className="mt-1 rounded-xl" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setView("main")} className="rounded-xl">Kembali</Button>
              <Button data-testid="save-session-reschedule-btn" disabled={busy} onClick={saveRes} className="rounded-xl bg-violet-500 hover:bg-violet-600">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Pengganti"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
